'use client'

// React Imports
import { useState, useEffect, useRef } from 'react'

// Redux Imports
import { useSelector } from 'react-redux'

import type { RootState } from '@/redux-store'

// Utils Imports
import axiosInstance from '@/utils/AxiosInstance'

// Types Imports
import type { WeatherData, WeatherAPIResponse, LocationRequestParams } from '@/types/weather/weatherTypes'

// Cache de 15 minutos
const CACHE_DURATION = 15 * 60 * 1000

interface WeatherState {
  data: WeatherData | null
  loading: boolean
  error: string | null
  lastFetch: number | null
}

/**
 * Hook simplificado para dados do clima
 * Atualizado para nova estrutura da API com suporte a idiomas
 */
export const useWeatherSimple = (language: string) => {
  const location = useSelector((state: RootState) => state.auth.location)

  const [state, setState] = useState<WeatherState>({
    data: null,
    loading: false,
    error: null,
    lastFetch: null
  })

  const hasRequestedRef = useRef(false)
  const fetchWeatherDataRef = useRef<(() => Promise<void>) | null>(null)

  // Verificar se tem localização válida
  const hasValidLocation = () => {
    return !!(location && location.granted && location.latitude !== 0 && location.longitude !== 0)
  }

  // Verificar se cache é válido
  const isCacheValid = () => {
    return state.lastFetch && Date.now() - state.lastFetch < CACHE_DURATION
  }

  // Função para buscar dados do clima
  fetchWeatherDataRef.current = async () => {
    if (!hasValidLocation() || state.loading || isCacheValid()) {
      return
    }

    setState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const params: LocationRequestParams = {
        latitude: location!.latitude,
        longitude: location!.longitude,
        language
      }

      const response = await axiosInstance.post<WeatherAPIResponse>(
        `${process.env.NEXT_PUBLIC_DASHBOARDS_PATH || ''}/v1/weather/location`,
        params,
        { timeout: 15000 }
      )

      if (response.data?.success && response.data?.data) {
        setState(prev => ({
          ...prev,
          data: response.data.data,
          loading: false,
          error: null,
          lastFetch: Date.now()
        }))
      } else {
        throw new Error('Resposta inválida da API')
      }
    } catch (error: any) {
      console.error('❌ Erro ao buscar dados do clima:', error)

      setState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || 'Erro ao carregar dados do clima',
        lastFetch: null
      }))
    }
  }

  // Effect para fazer requisição inicial
  useEffect(() => {
    // Fazer requisição se:
    // 1. Tem localização válida
    // 2. Ainda não fez requisição
    // 3. Não está carregando
    // 4. Não tem dados válidos em cache
    if (hasValidLocation() && !hasRequestedRef.current && !state.loading && !isCacheValid()) {
      hasRequestedRef.current = true
      fetchWeatherDataRef.current?.()
    }
  }, [location, language]) // eslint-disable-line react-hooks/exhaustive-deps

  // Reset quando idioma mudar
  useEffect(() => {
    if (language && state.data) {
      hasRequestedRef.current = false
      setState(prev => ({ ...prev, data: null, lastFetch: null }))
    }
  }, [language]) // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = () => {
    hasRequestedRef.current = false
    fetchWeatherDataRef.current?.()
  }

  return {
    data: state.data,
    loading: state.loading,
    error: state.error,
    hasValidLocation: hasValidLocation(),
    refetch,
    language
  }
}
